import * as React from "react";
import Stack from "@mui/material/Stack";
import { GridDensity } from "@mui/x-data-grid";
import {
  DataGridPro,
  GridPaginationModel,
  GridRowParams,
  GridRowSelectionModel,
} from "@mui/x-data-grid-pro";
import { GridDefinition } from "./NzcDataGrid.type";
import { useAppState } from "context/appState.context";
import { useEffect, useState } from "react";
import NzcCustomFilterToolbar from "./NzcCustomFilterToolbar";

/**
 * Component to display a message when no rows are found.
 */
const NoRowOverlay = () => (
  <Stack height="100%" alignItems="center" justifyContent="center">
    No rows found
  </Stack>
);

/**
 * NzcDataGrid component to display data in a table format with customizable
 * columns and rows. Converted from DataGrid (community) → DataGridPro.
 */
export default function NzcDataGrid(props: Readonly<GridDefinition>) {
  const {
    rows,
    columns,
    selectedRows,
    setSelectedRows,
    onSelectionChange,
    disableCheckBox = false,
    selectionModel,
    getRowId,
    sx,
    disableRowSelectionOnClick,
    userRoleParam,
    checkBoxSelection = false,
    enableFiltering = false,
    pinnedColumns,
    pagination,
    autoHeight = false,
    pageSizeOptions = [5, 10, 15, 20],
    columnVisibility,
    pinnedBottomRow = [],
    isRowSelectable,
    treeData,
    getTreeDataPath,
    groupingColDef,
    sortModel,
    rowSelectionPropagation,
    onPaginationModelChange,
  } = props;

  const { userRole } = useAppState();

  const [density, setDensity] = useState<GridDensity>("standard");
  const defaultPaginationModel: GridPaginationModel = {
    page: 0,
    pageSize: 10,
  };

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(
    props.paginationModel ?? defaultPaginationModel
  );

  function onRowSelection(row: GridRowSelectionModel) {
    const filtered = rows?.filter((item: any) => row.includes(item.nzdmId));
    if (onSelectionChange && !disableCheckBox) {
      onSelectionChange(filtered ? [...filtered] : []);
    }
  }

  const toolbarComponent = () =>
    enableFiltering ? <NzcCustomFilterToolbar gridDefinition={props} /> : null;

  useEffect(() => {
    setDensity(props.density ?? "standard");
  }, [props.density]);

  useEffect(() => {
    setPaginationModel(props.paginationModel ?? defaultPaginationModel);
  }, [props.paginationModel]);

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    setPaginationModel(model);
    onPaginationModelChange?.(model);
  };

  return (
    <div
      style={{
        height: autoHeight ? "auto" : "75vh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <DataGridPro
        rows={rows ?? []}
        columns={columns}
        columnBufferPx={20}
        autoHeight={autoHeight}
        sx={{
          ...sx,
          "& .MuiDataGrid-columnHeaderTitle": { fontSize: 12 },
          "& .MuiDataGrid-cell": { fontSize: 12 },
        }}
        getRowId={getRowId}
        disableRowSelectionOnClick={disableRowSelectionOnClick}
        onRowSelectionModelChange={onRowSelection}
        checkboxSelection={checkBoxSelection}
        onRowClick={(row: GridRowParams) => {
          if (
            disableCheckBox &&
            selectedRows !== undefined &&
            setSelectedRows
          ) {
            if (
              selectedRows.length > 0 &&
              selectedRows.some((x) => x.nzdmId === row.id)
            ) {
              setSelectedRows(
                [...selectedRows].filter((x) => x.nzdmId !== row.id)
              );
            } else if (!selectedRows.some((x) => x.nzdmId === row.id)) {
              setSelectedRows([...selectedRows, row.row]);
            }
          }
        }}
        rowSelectionModel={selectionModel}
        initialState={{
          pagination: {
            paginationModel: props.paginationModel ?? defaultPaginationModel,
          },
        }}
        columnVisibilityModel={{
          ...(columnVisibility ?? {}),
          actions: userRole !== userRoleParam,
        }}
        pinnedColumns={pinnedColumns}
        pinnedRows={{
          bottom: pinnedBottomRow,
        }}
        density={density}
        slots={{
          toolbar: toolbarComponent,
          noRowsOverlay: NoRowOverlay,
        }}
        onDensityChange={(newDensity) => setDensity(newDensity)}
        pagination={pagination}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        hideFooterPagination={false}
        pageSizeOptions={pageSizeOptions}
        isRowSelectable={isRowSelectable}
        treeData={treeData}
        getTreeDataPath={getTreeDataPath}
        groupingColDef={groupingColDef}
        sortModel={sortModel}
        rowSelectionPropagation={rowSelectionPropagation}
      />
    </div>
  );
}